import { useEffect, useMemo, useState } from "react";
import { api, API, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import {
  GraduationCap, PlayCircle, CheckCircle2, Download, Clock,
  Trophy, BookOpen, Plus, ShieldCheck, Award as AwardIcon
} from "lucide-react";

const STATUS_STYLE = {
  Enrolled: "bg-slate-100 text-slate-700",
  "In Progress": "bg-red-50 text-red-700",
  Completed: "bg-emerald-50 text-emerald-700",
  Dropped: "bg-red-50 text-red-700",
};

function downloadCert(id, filename) {
  const token = localStorage.getItem("access_token");
  fetch(`${API}/training/certificate/${id}`, { headers: { Authorization: `Bearer ${token}` }, credentials: "include" })
    .then(r => { if (!r.ok) throw new Error("Certificate not ready"); return r.blob(); })
    .then(blob => {
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click(); link.remove();
    })
    .catch(e => toast.error(e.message));
}

export default function Training() {
  const { user } = useAuth();
  const isReviewer = user?.role === "admin" || user?.role === "hr";
  const [courses, setCourses] = useState([]);
  const [mine, setMine] = useState([]);
  const [allEnroll, setAllEnroll] = useState([]);
  const [summary, setSummary] = useState(null);
  const [openCourse, setOpenCourse] = useState(null);
  const [openEnrollment, setOpenEnrollment] = useState(null);
  const [newCourseOpen, setNewCourseOpen] = useState(false);
  const [courseForm, setCourseForm] = useState({ title:"", vendor:"", level:"Foundation", category:"Technical", duration_hours:4, description:"", modules:"", linked_certification:"" });

  const load = async () => {
    const [c, m, s] = await Promise.all([
      api.get("/training/courses"),
      user?.employee_id ? api.get("/training/mine") : Promise.resolve({ data: [] }),
      api.get("/training/summary"),
    ]);
    setCourses(c.data); setMine(m.data); setSummary(s.data);
    if (isReviewer) setAllEnroll((await api.get("/training/enrollments")).data);
  };
  useEffect(() => { load(); }, [user?.employee_id, isReviewer]);

  const enroll = async (course) => {
    try { await api.post(`/training/enroll/${course.id}`); toast.success(`Enrolled in ${course.title}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const markModule = async (module) => {
    if (!openEnrollment) return;
    try {
      const r = await api.put(`/training/enroll/${openEnrollment.id}/progress`, { module });
      setOpenEnrollment(r.data);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const finish = async () => {
    if (!openEnrollment) return;
    try {
      const r = await api.post(`/training/enroll/${openEnrollment.id}/complete`, { final_score: 100 });
      setOpenEnrollment(r.data);
      toast.success("Course completed — certificate ready");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const createCourse = async (e) => {
    e.preventDefault();
    try {
      await api.post("/training/courses", {
        ...courseForm,
        duration_hours: Number(courseForm.duration_hours || 0),
        modules: courseForm.modules.split(",").map(s => s.trim()).filter(Boolean),
      });
      toast.success("Course published");
      setNewCourseOpen(false);
      setCourseForm({ title:"", vendor:"", level:"Foundation", category:"Technical", duration_hours:4, description:"", modules:"", linked_certification:"" });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const enrolledIds = new Set(mine.map(e => e.course_id));

  return (
    <div className="space-y-6" data-testid="training-root">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase font-semibold tracking-[0.1em] text-slate-500">Training Academy</div>
          <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tight text-slate-900">Level up, one course at a time.</h1>
          <p className="mt-1 text-sm text-slate-500">Enroll, learn, earn certificates — then feed them into your certification tracker.</p>
        </div>
        {isReviewer && (
          <Button onClick={() => setNewCourseOpen(true)} className="bg-[#DC2626] hover:bg-[#B91C1C]" data-testid="new-course-btn">
            <Plus className="h-4 w-4 mr-1.5" strokeWidth={1.8} /> New course
          </Button>
        )}
      </div>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Courses" value={summary.total_courses} icon={BookOpen} />
          <StatCard label="Enrollments" value={summary.total_enrollments} icon={GraduationCap} />
          <StatCard label="Completed" value={summary.by_status?.Completed || 0} icon={Trophy} tone="green" />
          <StatCard label="In progress" value={summary.by_status?.["In Progress"] || 0} icon={PlayCircle} tone="orange" />
        </div>
      )}

      <Tabs defaultValue={user?.employee_id ? "mine" : "catalog"}>
        <TabsList>
          {user?.employee_id && <TabsTrigger value="mine" data-testid="tab-my-learning">My learning</TabsTrigger>}
          <TabsTrigger value="catalog" data-testid="tab-catalog">Course catalog</TabsTrigger>
          {isReviewer && <TabsTrigger value="team" data-testid="tab-team-training">Team training</TabsTrigger>}
        </TabsList>

        {user?.employee_id && (
          <TabsContent value="mine">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {mine.map(e => (
                <Card key={e.id} className="p-5 rounded-xl border-slate-200/60" data-testid={`enrollment-${e.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-semibold text-slate-900 truncate">{e.course_title}</div>
                        <Badge className={`border-0 ${STATUS_STYLE[e.status]}`}>{e.status}</Badge>
                      </div>
                      <div className="mt-2">
                        <Progress value={e.progress_percent} className="h-1.5" />
                        <div className="text-xs text-slate-500 mt-1">{e.progress_percent}% complete{e.final_score ? ` · scored ${e.final_score}%` : ""}</div>
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {e.status !== "Completed" && (
                          <Button size="sm" variant="outline" onClick={() => { const c = courses.find(x => x.id === e.course_id); setOpenCourse(c); setOpenEnrollment(e); }} data-testid={`resume-${e.id}`}>
                            <PlayCircle className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.8} /> Continue
                          </Button>
                        )}
                        {e.status === "Completed" && (
                          <Button size="sm" variant="outline" className="border-emerald-200 text-emerald-700"
                                  onClick={() => downloadCert(e.id, `certificate-${e.course_title.replace(/\s+/g,'-')}.pdf`)}
                                  data-testid={`cert-${e.id}`}>
                            <Download className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.8} /> Certificate
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
              {mine.length === 0 && <div className="md:col-span-2 text-sm text-slate-500 py-10 text-center border border-dashed rounded-xl">Enroll in a course from the catalog to get started.</div>}
            </div>
          </TabsContent>
        )}

        <TabsContent value="catalog">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {courses.map(c => (
              <Card key={c.id} className="p-5 rounded-xl border-slate-200/60 flex flex-col" data-testid={`course-${c.id}`}>
                <div className="flex items-start justify-between mb-2">
                  <Badge className="bg-slate-100 text-slate-700 border-0 text-[10px]">{c.category}</Badge>
                  <Badge className={
                    c.level === "Foundation" ? "bg-emerald-50 text-emerald-700 border-0"
                    : c.level === "Intermediate" ? "bg-orange-50 text-orange-700 border-0"
                    : "bg-red-50 text-red-700 border-0"
                  }>{c.level}</Badge>
                </div>
                <div className="font-semibold text-slate-900 flex-1">{c.title}</div>
                <div className="text-xs text-slate-500 mt-1 flex items-center gap-3">
                  {c.vendor && <span>{c.vendor}</span>}
                  <span className="flex items-center gap-1"><Clock className="h-3 w-3" strokeWidth={1.5} />{c.duration_hours}h</span>
                  <span>{c.modules?.length || 0} modules</span>
                </div>
                <p className="text-sm text-slate-600 mt-2 line-clamp-3">{c.description}</p>
                {c.linked_certification && (
                  <div className="mt-2 text-[11px] text-[#DC2626] flex items-center gap-1">
                    <ShieldCheck className="h-3 w-3" strokeWidth={2} />Prep for {c.linked_certification}
                  </div>
                )}
                <div className="mt-4">
                  {enrolledIds.has(c.id) ? (
                    <Button size="sm" variant="outline" disabled className="w-full">Enrolled</Button>
                  ) : (
                    user?.employee_id ? (
                      <Button size="sm" onClick={() => enroll(c)} className="bg-[#DC2626] hover:bg-[#B91C1C] w-full" data-testid={`enroll-${c.id}`}>Enroll</Button>
                    ) : (
                      <Button size="sm" variant="outline" onClick={() => setOpenCourse(c)} className="w-full">View syllabus</Button>
                    )
                  )}
                </div>
              </Card>
            ))}
          </div>
        </TabsContent>

        {isReviewer && (
          <TabsContent value="team">
            <Card className="rounded-xl border-slate-200/60 overflow-hidden">
              <div className="grid grid-cols-12 gap-4 px-6 py-3 border-b border-slate-100 text-[10px] uppercase font-semibold tracking-wider text-slate-500">
                <div className="col-span-4">Employee</div>
                <div className="col-span-4">Course</div>
                <div className="col-span-2">Status</div>
                <div className="col-span-2">Progress</div>
              </div>
              {allEnroll.map(e => (
                <div key={e.id} className="grid grid-cols-12 gap-4 px-6 py-3 border-b border-slate-50 items-center text-sm">
                  <div className="col-span-4 font-semibold text-slate-900">{e.employee_name}</div>
                  <div className="col-span-4 text-slate-700 truncate">{e.course_title}</div>
                  <div className="col-span-2"><Badge className={`border-0 ${STATUS_STYLE[e.status]}`}>{e.status}</Badge></div>
                  <div className="col-span-2">
                    <div className="flex items-center gap-2">
                      <Progress value={e.progress_percent} className="h-1.5 flex-1" />
                      <span className="text-xs w-9 text-right tabular-nums">{e.progress_percent}%</span>
                    </div>
                  </div>
                </div>
              ))}
              {allEnroll.length === 0 && <div className="p-10 text-center text-sm text-slate-500">No enrollments yet.</div>}
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Course detail + progression */}
      <Dialog open={!!openCourse} onOpenChange={(o) => { if (!o) { setOpenCourse(null); setOpenEnrollment(null); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{openCourse?.title}</DialogTitle></DialogHeader>
          {openCourse && (
            <div className="space-y-4">
              <div className="text-sm text-slate-600">{openCourse.description}</div>
              {openEnrollment && (
                <div>
                  <Progress value={openEnrollment.progress_percent} className="h-2" />
                  <div className="text-xs text-slate-500 mt-1">{openEnrollment.progress_percent}% complete</div>
                </div>
              )}
              <div>
                <div className="text-xs uppercase font-semibold tracking-wider text-slate-500 mb-2">Modules</div>
                <div className="space-y-2">
                  {openCourse.modules.map(m => {
                    const done = openEnrollment?.modules_done?.includes(m);
                    return (
                      <div key={m} className={`flex items-center justify-between p-3 rounded-lg border ${done ? "border-emerald-200 bg-emerald-50/50" : "border-slate-200"}`}>
                        <div className="flex items-center gap-2 text-sm">
                          {done ? <CheckCircle2 className="h-4 w-4 text-emerald-600" strokeWidth={2} /> : <BookOpen className="h-4 w-4 text-slate-400" strokeWidth={1.5} />}
                          <span className={done ? "text-slate-900 font-semibold" : "text-slate-700"}>{m}</span>
                        </div>
                        {openEnrollment && !done && openEnrollment.status !== "Completed" && (
                          <Button size="sm" variant="outline" onClick={() => markModule(m)} data-testid={`mark-${openEnrollment.id}-${m.slice(0,10)}`}>Mark done</Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
              {openEnrollment && openEnrollment.status !== "Completed" && openEnrollment.progress_percent === 100 && (
                <Button onClick={finish} className="bg-emerald-600 hover:bg-emerald-700 w-full" data-testid="complete-course-btn">
                  <Trophy className="h-4 w-4 mr-1.5" strokeWidth={1.8} /> Finish course & issue certificate
                </Button>
              )}
              {openEnrollment?.status === "Completed" && (
                <Button onClick={() => downloadCert(openEnrollment.id, `certificate-${openCourse.title.replace(/\s+/g,'-')}.pdf`)} className="bg-[#DC2626] hover:bg-[#B91C1C] w-full">
                  <Download className="h-4 w-4 mr-1.5" strokeWidth={1.8} /> Download certificate
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* New course */}
      <Dialog open={newCourseOpen} onOpenChange={setNewCourseOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Create course</DialogTitle></DialogHeader>
          <form onSubmit={createCourse} className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <FormField label="Title *"><Input required value={courseForm.title} onChange={e => setCourseForm({...courseForm, title: e.target.value})} data-testid="course-title" /></FormField>
            <FormField label="Vendor"><Input value={courseForm.vendor} onChange={e => setCourseForm({...courseForm, vendor: e.target.value})} placeholder="VMware / AWS / Cisco" /></FormField>
            <FormField label="Level">
              <Select value={courseForm.level} onValueChange={v => setCourseForm({...courseForm, level: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{["Foundation","Intermediate","Advanced"].map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </FormField>
            <FormField label="Category">
              <Select value={courseForm.category} onValueChange={v => setCourseForm({...courseForm, category: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{["Technical","Business","Compliance","Leadership","Onboarding"].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </FormField>
            <FormField label="Duration (hours)"><Input type="number" step="0.5" value={courseForm.duration_hours} onChange={e => setCourseForm({...courseForm, duration_hours: e.target.value})} /></FormField>
            <FormField label="Linked certification"><Input value={courseForm.linked_certification} onChange={e => setCourseForm({...courseForm, linked_certification: e.target.value})} placeholder="AWS SAA" /></FormField>
            <div className="md:col-span-2"><FormField label="Description *"><Textarea rows={3} required value={courseForm.description} onChange={e => setCourseForm({...courseForm, description: e.target.value})} /></FormField></div>
            <div className="md:col-span-2"><FormField label="Modules (comma separated)"><Textarea rows={2} value={courseForm.modules} onChange={e => setCourseForm({...courseForm, modules: e.target.value})} placeholder="Intro, Networking, Storage" /></FormField></div>
            <div className="md:col-span-2 flex justify-end"><Button type="submit" className="bg-[#DC2626] hover:bg-[#B91C1C]" data-testid="course-submit">Publish course</Button></div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, tone = "red" }) {
  const tones = { red: "text-[#DC2626] bg-red-50", green: "text-emerald-600 bg-emerald-50", orange: "text-orange-600 bg-orange-50" };
  return (
    <Card className="p-5 rounded-xl border-slate-200/60">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] uppercase font-semibold tracking-wider text-slate-500">{label}</div>
          <div className="mt-1 font-display text-2xl font-bold text-slate-900">{value}</div>
        </div>
        <div className={`h-10 w-10 rounded-lg grid place-items-center ${tones[tone]}`}><Icon className="h-5 w-5" strokeWidth={1.5} /></div>
      </div>
    </Card>
  );
}

function FormField({ label, children }) {
  return <div className="space-y-1.5"><Label>{label}</Label>{children}</div>;
}
